import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';

import { ConfigurationResponse, GuestDataResponse } from 'app/services/GuestCenterService.types';
import Logger from 'app/services/Logger';
import { QueryKeys } from 'app/services/QueryClient';

const MODULE = '[useProductLineupValidation]';

// Maximum number of invalidation attempts to prevent infinite loops
const MAX_INVALIDATION_ATTEMPTS = 3;
// Cooldown period between invalidations (5 minutes in production)
const INVALIDATION_COOLDOWN_MS = 5 * 60 * 1000;
// Initial delay before starting validation (to allow app to load)
const INITIAL_VALIDATION_DELAY_MS = 3 * 1000; // 3 seconds

/**
 * Hook that validates guest data passes against configuration product lineups
 * and automatically invalidates cache if mismatches are found.
 *
 * Includes safeguards to prevent infinite loops when server-side issues persist.
 * Non-blocking: allows app to function normally while validation runs in background.
 */
export default function useProductLineupValidation({
  guestDataResponse,
  configurationResponse,
}: {
  guestDataResponse: GuestDataResponse | undefined;
  configurationResponse: ConfigurationResponse | undefined;
}) {
  const queryClient = useQueryClient();
  const invalidationAttempts = useRef(0);
  const lastInvalidationTime = useRef<number>(0);
  const cooldownTimer = useRef<NodeJS.Timeout | null>(null);
  const lastSeenConfigHash = useRef<string>('');
  const hasInitialDelayPassed = useRef(false);
  const [forceValidation, setForceValidation] = useState(0);

  useEffect(() => {
    // Only validate when both responses are available
    if (!guestDataResponse || !configurationResponse) {
      return;
    }

    // Add initial delay to allow app to load properly before starting validation
    if (!hasInitialDelayPassed.current) {
      Logger.debug(
        `${MODULE} Delaying initial validation for ${INITIAL_VALIDATION_DELAY_MS}ms to allow app startup`
      );

      const initialTimer = setTimeout(() => {
        hasInitialDelayPassed.current = true;
        setForceValidation((prev) => prev + 1);
      }, INITIAL_VALIDATION_DELAY_MS);

      // eslint-disable-next-line consistent-return
      return () => clearTimeout(initialTimer);
    }

    const passes = guestDataResponse.guestData?.passes || [];
    const productLineups = configurationResponse.productLineups?.configuration || [];
    const availableLineupKeys = new Set(productLineups.map((pl) => pl.productLineupKey));

    // Find passes with productLineupKeys that don't exist in configuration
    const invalidPasses = passes.filter((pass) => {
      const lineupKey = pass.productLineupKey;
      return lineupKey && !availableLineupKeys.has(lineupKey);
    });

    if (invalidPasses.length > 0) {
      const now = Date.now();
      const timeSinceLastInvalidation = now - lastInvalidationTime.current;

      const isNearMaxAttempts = invalidationAttempts.current >= MAX_INVALIDATION_ATTEMPTS - 1;

      const logMessage = `${MODULE} Found ${invalidPasses.length} passes with invalid productLineupKeys`;
      const logData = {
        invalidLineupKeys: invalidPasses.map((p) => p.productLineupKey),
        availableLineupKeys: Array.from(availableLineupKeys),
        invalidationAttempts: invalidationAttempts.current,
        maxAttempts: MAX_INVALIDATION_ATTEMPTS,
      };

      // Log with appropriate level
      if (invalidationAttempts.current >= MAX_INVALIDATION_ATTEMPTS) {
        Logger.error(logMessage, logData);
      } else if (isNearMaxAttempts) {
        Logger.warn(logMessage, logData);
      } else {
        Logger.info(logMessage, logData);
      }

      // Check if we should attempt invalidation
      if (
        invalidationAttempts.current < MAX_INVALIDATION_ATTEMPTS &&
        timeSinceLastInvalidation > INVALIDATION_COOLDOWN_MS
      ) {
        Logger.info(
          `${MODULE} Starting background self-healing: invalidating cache (attempt ${
            invalidationAttempts.current + 1
          }/${MAX_INVALIDATION_ATTEMPTS})`
        );

        // Use background invalidation to avoid blocking the UI
        setTimeout(() => {
          queryClient.invalidateQueries([QueryKeys.Configuration]);
        }, 100); // Small delay to ensure current render completes

        invalidationAttempts.current += 1;
        lastInvalidationTime.current = now;

        // Schedule next validation attempt after cooldown
        const timeoutMs = INVALIDATION_COOLDOWN_MS + 1000;

        if (cooldownTimer.current) {
          clearTimeout(cooldownTimer.current);
        }

        cooldownTimer.current = setTimeout(() => {
          setForceValidation((prev) => prev + 1);
        }, timeoutMs);

        if (invalidationAttempts.current >= MAX_INVALIDATION_ATTEMPTS) {
          Logger.info(`${MODULE} Final attempt completed. Scheduling final validation check.`);
        }
      } else if (invalidationAttempts.current >= MAX_INVALIDATION_ATTEMPTS) {
        Logger.error(
          `${MODULE}: Max invalidation attempts reached. Server-side lineup mismatch requires manual intervention`,
          {
            invalidLineupKeys: invalidPasses.map((p) => p.productLineupKey),
            maxAttempts: MAX_INVALIDATION_ATTEMPTS,
            suggestion: 'Check server configuration or contact support',
          }
        );

        // Stop all retry attempts
        if (cooldownTimer.current) {
          clearTimeout(cooldownTimer.current);
          cooldownTimer.current = null;
        }
      } else {
        // Still in cooldown period - schedule validation when cooldown ends
        const remainingCooldown = INVALIDATION_COOLDOWN_MS - timeSinceLastInvalidation;
        if (remainingCooldown > 0 && invalidationAttempts.current < MAX_INVALIDATION_ATTEMPTS) {
          if (cooldownTimer.current) {
            clearTimeout(cooldownTimer.current);
          }

          cooldownTimer.current = setTimeout(() => {
            setForceValidation((prev) => prev + 1);
          }, remainingCooldown + 1000);
        }
      }
    } else {
      // All lineup keys are valid - reset attempts if needed
      if (invalidationAttempts.current > 0) {
        Logger.info(`${MODULE} Issue resolved! Resetting invalidation attempts`, {
          previousAttempts: invalidationAttempts.current,
        });
        invalidationAttempts.current = 0;
      }

      // Clear any pending timer
      if (cooldownTimer.current) {
        clearTimeout(cooldownTimer.current);
        cooldownTimer.current = null;
      }
    }
  }, [guestDataResponse, configurationResponse, queryClient, forceValidation]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimer.current) {
        clearTimeout(cooldownTimer.current);
        cooldownTimer.current = null;
      }
    };
  }, []);

  // Handle configuration hash changes - only reset when hash actually changes
  useEffect(() => {
    const configHash = configurationResponse?.productLineups?.configurationHash;

    if (!configHash) {
      return;
    }

    // Initialize hash on first load
    if (!lastSeenConfigHash.current) {
      lastSeenConfigHash.current = configHash;
      return;
    }

    // Check if hash actually changed
    if (configHash !== lastSeenConfigHash.current) {
      Logger.info(`${MODULE} Configuration updated - resetting invalidation attempts`, {
        previousAttempts: invalidationAttempts.current,
      });

      lastSeenConfigHash.current = configHash;
      invalidationAttempts.current = 0;

      // Clear any pending timer since we have new config
      if (cooldownTimer.current) {
        clearTimeout(cooldownTimer.current);
        cooldownTimer.current = null;
      }
    }
  }, [configurationResponse?.productLineups?.configurationHash]);
}
